export default {
  details: 'View details and examples',
  current: 'With the current selection',
  example: 'Illustrative example',
  holidayColumn: 'Holiday counting',
  columnTitles: {
    tenureStart: 'Starting service length',
    tenureEnd: 'Ending service length',
    entitlementDays: 'Entitlement days',
    isEntitleBlockLeave: 'Block Leave entitlement',
    blockLeaveReference: 'Block Leave sequence'
  },
  general: {
    summary:
      'Define the leave type and eligible employees, then configure its conditions and entitlement below.'
  },
  company: {
    summary:
      'The company that owns this leave type. It follows the company selected for configuration and cannot be changed here.'
  },
  status: {
    summary: 'Choose whether this leave type is available for use.',
    choices: [
      {
        value: 1,
        title: 'Active',
        description:
          'Enable this leave type. Employees must still meet its eligibility and entitlement conditions.'
      },
      {
        value: -1,
        title: 'Inactive',
        description:
          'Disable this leave type without deleting existing leave history.'
      }
    ]
  },
  code: {
    summary:
      'The reference code for this leave type. Use a code that clearly distinguishes it from other leave types.'
  },
  nameTH: {
    summary:
      'The leave type name shown when the interface uses Thai, for example ลาพักร้อน.'
  },
  nameEN: {
    summary:
      'The leave type name shown when the interface uses English, for example Annual Leave.'
  },
  leaveTypeColor: {
    summary:
      'The colour used to distinguish this leave type in views such as the calendar.'
  },
  gender: {
    summary:
      'Select eligible employees by the gender recorded in their employee data.',
    choices: [
      {
        value: 2,
        title: 'Male',
        description: 'Apply to employees recorded as male.'
      },
      {
        value: 3,
        title: 'Female',
        description: 'Apply to employees recorded as female.'
      },
      {
        value: 1,
        title: 'All genders',
        description: 'Do not restrict eligibility by gender.'
      }
    ]
  },
  officerType: {
    summary:
      'Select eligible employee categories using recorded employee status.',
    choices: [
      {
        value: 1,
        title: 'All employees',
        description: 'Include all employee status categories.'
      },
      {
        value: 2,
        title: 'Current employees',
        description:
          'Apply to employees whose recorded status is “Current employee”.'
      },
      {
        value: 3,
        title: 'New employees',
        description:
          'Apply to employees whose recorded status is “New employee”.'
      },
      {
        value: 4,
        title: 'Employees on probation',
        description:
          'Apply to employees whose recorded status is “On probation”.'
      },
      {
        value: 5,
        title: 'New employees and employees on probation',
        description:
          'Apply to both new employees and employees on probation, based on their recorded status.'
      }
    ]
  },
  leaveSetting: {
    summary:
      'Limit the number of leave requests, separately from their duration in days, hours or minutes. One request for three days counts as one request.',
    choices: [
      {
        value: 1,
        title: 'Lifetime maximum',
        description:
          'Use the cumulative request limit over the employment period.',
        example:
          'A limit of 3 means up to 3 requests accumulated for this leave type.'
      },
      {
        value: 2,
        title: 'Annual maximum',
        description: 'Use the request limit for the entitlement period year.',
        example:
          '2 requests per year is separate from the number of entitlement days in that year.'
      },
      {
        value: 4,
        title: 'No request count limit',
        description:
          'Do not use these two request count limits. Duration and other entitlement conditions still apply.'
      }
    ]
  },
  maxLeaveOverLifeTime: {
    summary:
      'The cumulative maximum number of requests over employment. Applies with the lifetime limit; this is not a number of leave days.'
  },
  maxLeavePerYear: {
    summary:
      'The maximum number of requests for an entitlement period year. Applies with the annual limit; this is not a number of leave days.'
  },
  minLeaveDayPerTime: {
    summary:
      'The minimum duration of one request. Enter a number and select its unit together, for example at least 2 hours.',
    choices: [
      {
        value: 1,
        title: 'Days',
        description:
          'Use days, converted using the configured working hours per day.'
      },
      {
        value: 2,
        title: 'Hours',
        description: 'Use hours, for example 2 hours = 120 minutes.'
      },
      {
        value: 3,
        title: 'Minutes',
        description: 'Use a duration in minutes, for example 30 minutes.'
      }
    ]
  },
  maxLeaveDayPerTime: {
    summary:
      'The maximum duration of one request. Enter a number and unit; this is separate from request counts and total entitlement.',
    choices: [
      {
        value: 1,
        title: 'Days',
        description:
          'Limit days using configured working hours per day, for example up to 5 days per request.'
      },
      {
        value: 2,
        title: 'Hours',
        description: 'Limit hours, for example up to 4 hours per request.'
      },
      {
        value: 3,
        title: 'Minutes',
        description: 'Limit minutes, for example up to 90 minutes per request.'
      }
    ]
  },
  blockLeaveType: {
    summary:
      'Choose whether to enable Block Leave, with a date window and minimum leave days for each block.',
    choices: [
      {
        value: true,
        title: 'Selected',
        description:
          'Enable Block Leave, configure its windows below and link a block sequence to entitlement.'
      },
      {
        value: false,
        title: 'Not selected',
        description:
          'Disable Block Leave. Table entries alone do not enable it.'
      }
    ]
  },
  blockGeneral: {
    summary:
      'Each row defines one Block Leave window. A request must fall entirely within exactly one matching window. Use +/− to add or remove windows.'
  },
  blockSequence: {
    summary:
      'The block reference number, used to link this window to Block Leave entitlement below.'
  },
  periodStart: {
    summary: 'The starting day and month of this window, for example 1 April.'
  },
  periodEnd: {
    summary: 'The ending day and month of this window, for example 30 June.'
  },
  blockMinimum: {
    summary:
      'The minimum number of leave days for Block Leave in this window, not the number of requests.'
  },
  includeDayOff: {
    summary:
      'Select how holidays count for this block. This replaces the day counting method for ordinary requests.',
    choices: [
      {
        value: true,
        title: 'Include holidays',
        description: 'Count both weekly days off and public holidays.',
        example: 'A 5-day window containing 2 holidays counts as 5 leave days.'
      },
      {
        value: false,
        title: 'Exclude holidays',
        description: 'Exclude both weekly days off and public holidays.',
        example:
          'A 5-day window containing 2 distinct holidays counts as 3 leave days.'
      }
    ]
  },
  workTimeCalculationType: {
    summary:
      'Select the starting date for service length, used for entitlement bands and proportional calculation. This is separate from when entitlement is granted.',
    choices: [
      {
        value: 1,
        title: 'Employment start date',
        description: 'Count service from the recorded employment start date.'
      },
      {
        value: 2,
        title: 'Probation completion date',
        description:
          'Count service from the recorded probation completion date.'
      }
    ]
  },
  permissionLeave: {
    summary: 'Choose when leave entitlement is granted.',
    choices: [
      {
        value: 1,
        title: 'Leave period',
        description:
          'Grant entitlement according to the configured calendar or custom leave period.',
        example:
          'A calendar period starts on 1 January; a custom period could start on 1 April.'
      },
      {
        value: 2,
        title: 'Employment start date',
        description:
          'Use the employment start date as the eligibility trigger. A valid recorded start date must have been reached.',
        example:
          'For an employee starting on 1 April, this uses the employment start date rather than probation completion.'
      },
      {
        value: 3,
        title: 'Probation completion date',
        description:
          'Eligibility starts when the recorded probation completion date is reached.',
        example:
          'If employment starts in April and probation ends in July, the probation completion date is the trigger.'
      },
      {
        value: 4,
        title: 'Beginning of each month',
        description:
          'Process entitlement on the first day of the month. The amount depends on the table and calculation method; it does not automatically mean annual entitlement divided by 12.'
      },
      {
        value: 5,
        title: 'End of each month',
        description:
          'Process entitlement on the last day of the month. The grant date differs from the beginning-of-month option.'
      },
      {
        value: 7,
        title: 'First leave request',
        description:
          'Use the first-leave entitlement mode, with a day limit for the first request. This is not a waiting period after joining.',
        example:
          'Entering 3 days limits the first request to 3 days; other conditions still apply.'
      }
    ]
  },
  permissionLeaveDay: {
    summary:
      'For the first-leave option, limit the first request duration. Day requests count the inclusive date span, including holidays; hour requests use working hours per day for conversion. This is not an entitlement waiting period.'
  },
  permissionCalculation: {
    summary:
      'Choose how to calculate the entitlement amount from service bands. This is separate from when entitlement is granted.',
    choices: [
      {
        value: 1,
        title: 'Full entitlement',
        description:
          'Use the applicable table amount without reducing it proportionally for service length.',
        example: 'A table entitlement of 10 days uses a base of 10 days.'
      },
      {
        value: 2,
        title: 'Proportional entitlement',
        description:
          'When calculated service is under 12 months, use days from the service start to the reference date ÷ 365 × table entitlement, then apply the selected rounding method.',
        example:
          'A 10-day base over 146 calculated days gives 10 × 146 ÷ 365 = 4 days before rounding.'
      }
    ]
  },
  monthRound: {
    summary:
      'Adjust the service start date using the table window that matches its day of the month. These inputs are editable when proportional calculation is selected.',
    choices: [
      {
        value: 1,
        title: 'No month rounding',
        description:
          'Keep the original service start date without applying this table.'
      },
      {
        value: 2,
        title: 'Month rounding',
        description:
          'If the starting day falls within a configured range, move the service start date backwards by the specified number of days.',
        example:
          'A start on day 10 matches a range of 1–15 with 5 rounding days: move the service start backwards by 5 days.'
      }
    ]
  },
  rowOrder: {
    summary: 'The row number. Use +/− to add or remove table conditions.'
  },
  roundStart: {
    summary:
      'The first day number in the range, for example 1 means the first day of the month, not leave days.'
  },
  roundEnd: {
    summary:
      'The last day number in the range, for example 15 means the fifteenth day of the month.'
  },
  roundDays: {
    summary:
      'The number of days to move the service start date backwards when this range matches.'
  },
  roundType: {
    summary:
      'Round the calculated entitlement in days, especially when using proportional calculation.',
    choices: [
      {
        value: 1,
        title: 'No rounding',
        description: 'Keep the calculated amount.',
        example: '2.2 days remains 2.2 days.'
      },
      {
        value: 2,
        title: 'Round down',
        description: 'Discard the fractional part to keep whole days.',
        example: '2.8 days becomes 2 days.'
      },
      {
        value: 7,
        title: 'Policy rounding',
        description:
          'Round the fractional part to two decimal places before comparison: below 0.5 becomes half a day, exactly 0.5 keeps the original amount, and above 0.5 rounds up to a whole day.',
        example: '2 → 2.5 days; 2.2 → 2.5 days; 2.5 → 2.5 days; 2.8 → 3 days.'
      }
    ]
  },
  leaveDayCalculationType: {
    summary:
      'Choose which holidays count as leave for ordinary requests, using the employee holiday calendar and work schedule.',
    choices: [
      {
        value: 1,
        title: 'Exclude all holidays',
        description: 'Exclude weekly days off and public holidays.',
        example: '5 days with 2 distinct holidays counts as 3 leave days.'
      },
      {
        value: 2,
        title: 'Include weekly days off',
        description: 'Count weekly days off but exclude public holidays.',
        example:
          '5 days with 1 weekly day off and 1 separate public holiday counts as 4 leave days.'
      },
      {
        value: 3,
        title: 'Include all holidays',
        description: 'Count weekly days off and public holidays.',
        example: 'A 5-day window counts as 5 leave days.'
      }
    ]
  },
  entitlementTable: {
    summary:
      'Configure entitlement by organisation, position, level and service length. Each group has its own carry-forward rules. Use +/− to add or remove groups or service bands.'
  },
  organize: {
    summary: 'Select the organisational units that use this entitlement group.'
  },
  position: {
    summary:
      'Select applicable job positions, together with organisation and job level conditions. These are employee positions, not approver positions.'
  },
  level: {
    summary:
      'Select applicable job levels, together with organisation and job position conditions.'
  },
  tenureStart: {
    summary:
      'Starting service length: enter years and months together. For example, 1 year 6 months = 18 months, measured from the selected service start date.'
  },
  tenureEnd: {
    summary:
      'Ending service length: record the ending years and months, not an entitlement expiry date. Current grants use the starting service threshold of applicable rows.'
  },
  entitlementDays: {
    summary:
      'The base entitlement in days when this row’s starting service threshold is reached, for example 10 days. Grant timing, calculation and rounding also apply.'
  },
  isEntitleBlockLeave: {
    summary: 'Choose whether this service band is linked to Block Leave.',
    choices: [
      {
        value: true,
        title: 'Selected',
        description:
          'Link a Block Leave window by entering its sequence number in the next field.'
      },
      {
        value: false,
        title: 'Not selected',
        description: 'Do not link this service band to Block Leave.'
      }
    ]
  },
  blockLeaveReference: {
    summary:
      'Enter a Block Leave sequence from the table above. For example, 2 means block number 2. This is a reference number, not a day amount, even though the existing field suffix says “days”.'
  },
  forwardType: {
    summary:
      'Choose how unused entitlement transfers from the previous period to the new period. This is separate from allowing requests across years.',
    choices: [
      {
        value: 1,
        title: 'No carry-forward',
        description:
          'Do not transfer unused entitlement from the previous period.'
      },
      {
        value: 2,
        title: 'Carry forward all',
        description: 'Transfer all remaining entitlement.',
        example: '4 unused days transfers 4 days.'
      },
      {
        value: 3,
        title: 'Limited accumulated carry-forward',
        description:
          'Transfer unused entitlement up to the combined day and hour limit entered.',
        example:
          'With 4 unused days and a cap of 2 days 4 hours, transfer no more than 2 days 4 hours.'
      }
    ]
  },
  forwardLimit: {
    summary:
      'The carry-forward cap combines days and hours into one limit. For example, 2 days plus 4 hours. Days use configured working hours per day; these are not two separate caps.'
  },
  expireType: {
    summary:
      'Choose the window in which carried entitlement can be used. This is separate from forbidden dates and the new leave period.',
    choices: [
      {
        value: 1,
        title: 'Use on any day',
        description:
          'Do not set a specific day/month window for carried entitlement.'
      },
      {
        value: 2,
        title: 'Use within specified dates',
        description:
          'Use carried entitlement within the day/month window below.',
        example:
          '1 January to 31 March allows use of carried entitlement in that window.'
      }
    ]
  },
  approvalSettings: {
    summary:
      'Configure requests exceeding entitlement and forbidden date windows. Other leave type conditions still apply.'
  },
  isAllowOverdue: {
    summary:
      'Choose whether a request may exceed the remaining entitlement balance. This is separate from requests across years.',
    choices: [
      {
        value: true,
        title: 'Allow',
        description:
          'Allow leave exceeding entitlement while still checking other conditions, such as request count and duration limits.'
      },
      {
        value: false,
        title: 'Do not allow',
        description:
          'Ordinary requests require sufficient entitlement. If emergency leave is enabled, its conditions apply as well.'
      }
    ]
  },
  isActiveForbiddenDate: {
    summary:
      'Enable or disable forbidden leave windows. Define a starting and ending day/month for each window.',
    choices: [
      {
        value: true,
        title: 'Selected',
        description: 'Check requests against the forbidden windows below.'
      },
      {
        value: false,
        title: 'Not selected',
        description:
          'Do not apply this forbidden-window table. Its day/month inputs are disabled.'
      }
    ]
  },
  prospectLeave: {
    summary:
      'Set the minimum advance notice in calendar days, counting the submission date as day 1.',
    choices: [
      {
        value: false,
        title: 'Not selected',
        description: 'Do not apply the advance day requirement from this field.'
      },
      {
        value: true,
        title: 'Selected',
        description:
          'Both the start and end dates must meet the required notice period, except retrospective requests accepted under the retrospective rules.',
        example:
          'Submit on 1 April with 3 days of notice configured: leave may start from 3 April under this rule.'
      }
    ]
  },
  retrospectLeave: {
    summary:
      'Set the retrospective date boundary in calendar days before submission.',
    choices: [
      {
        value: false,
        title: 'Not selected',
        description:
          'Do not apply a retrospective day limit from this field. Other date conditions still apply.'
      },
      {
        value: true,
        title: 'Selected',
        description:
          'Enter allowed retrospective days. Neither request date may be earlier than the submission date minus this number.',
        example: 'Submit on day 10 and enter 3 days: the boundary is day 7.'
      }
    ]
  },
  isRequireAttachment: {
    summary:
      'Choose whether supporting files are required for a request. This setting does not define file size or file types.',
    choices: [
      {
        value: true,
        title: 'Required',
        description: 'The request must include a supporting attachment.'
      },
      {
        value: false,
        title: 'Optional',
        description: 'This setting does not require an attachment.'
      }
    ]
  },
  isRequireReason: {
    summary:
      'Choose whether employees must enter a reason before submitting a request.',
    choices: [
      {
        value: true,
        title: 'Required',
        description: 'The request must include a leave reason.'
      },
      {
        value: false,
        title: 'Optional',
        description: 'This setting does not require a reason.'
      }
    ]
  },
  reasonTextHeader: {
    summary:
      'The guidance shown in the reason field, for example “Please provide the reason and supporting information”. This is a prompt, not the actual request reason.'
  },
  isAllowOverLeave: {
    summary:
      'Configure the use of entitlement to request leave in the next year. This is separate from transferring unused entitlement to a new period.',
    choices: [
      {
        value: true,
        title: 'Allow',
        description:
          'Enable requests across years using the supported entitlement periods. This does not automatically carry forward unused entitlement.'
      },
      {
        value: false,
        title: 'Do not allow',
        description:
          'Do not enable requests across years through this setting. Carry-forward remains a separate condition.'
      }
    ]
  },
  isAllowUrgentLeave: {
    summary:
      'Enable emergency leave, which affects entitlement balance checks. It does not bypass all leave type conditions.',
    choices: [
      {
        value: true,
        title: 'Allow',
        description:
          'Allow the supported emergency option. Dates, request count, duration and other conditions are still checked.'
      },
      {
        value: false,
        title: 'Do not allow',
        description: 'Do not enable the emergency option for this leave type.'
      }
    ]
  },
  inMonthCondition: {
    summary:
      'Restrict leave months using birthdays recorded in employee or family data.',
    choices: [
      {
        value: 1,
        title: 'No birthday restriction',
        description: 'Do not apply a birthday month condition.'
      },
      {
        value: 2,
        title: 'Employee birthday month',
        description: 'Allow leave in the employee birthday month.',
        example: 'An employee born in May requests leave in May.'
      },
      {
        value: 3,
        title: 'Spouse birthday month',
        description:
          'Allow leave in the spouse birthday month recorded in family data.'
      },
      {
        value: 4,
        title: 'Child birthday month',
        description:
          'Allow leave in a child birthday month recorded in family data. With multiple children, use the recorded birthday months.'
      }
    ]
  },
  payment: {
    summary:
      'Choose whether this leave type is paid and set the maximum duration of paid leave.',
    choices: [
      {
        value: false,
        title: 'Unpaid',
        description: 'Treat this leave type as unpaid leave.'
      },
      {
        value: true,
        title: 'Paid',
        description:
          'Specify the maximum duration of paid leave in the following fields.'
      }
    ]
  },
  paymentDuration: {
    summary:
      'Enter the maximum duration of paid leave and select its unit, for example 30 days.',
    choices: [
      {
        value: 1,
        title: 'Days',
        description: 'Specify paid duration in days.'
      },
      {
        value: 2,
        title: 'Hours',
        description: 'Specify paid duration in hours.'
      },
      {
        value: 3,
        title: 'Minutes',
        description: 'Specify paid duration in minutes.'
      }
    ]
  },
  leavePeriod: {
    summary:
      'Define the entitlement period dates, used with period-based grants. This is separate from forbidden windows.',
    choices: [
      {
        value: 1,
        title: 'Calendar year',
        description: '1 January to 31 December. The system sets both dates.'
      },
      {
        value: 2,
        title: 'Custom period',
        description:
          'Choose the starting day and month. The ending date is the day before the next period starts.',
        example: 'A 1 April start gives a 31 March end in the following year.'
      }
    ]
  },
  leavePeriodDates: {
    summary:
      'The left day/month fields define the start; the right fields show the calculated end. Calendar dates cannot be edited; only the start is editable for custom periods.'
  },
  order: {
    summary: 'The sequence used to order leave types for display.'
  },
  approveWorkflow: {
    summary:
      'Select the existing Workflow that defines the approval hierarchy and sequence for requests of this leave type.'
  }
}
